/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/pinListsApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API service functions for PIN Lists.
    Centralized API calls for better maintainability.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  PinList,
  ListPinListsParams,
  ListPinListsResponse,
  PinListResponse,
  CreatePinListPayload,
  UpdatePinListPayload
} from '../types';

/**
 * Fetch PIN Lists with pagination and filters
 */
export async function fetchPinLists(
  params: ListPinListsParams
): Promise<ListPinListsResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search ? { search: params.search } : {})
  });

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/telephony/pin-lists?${queryParams}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch PIN lists');
  }

  const result = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch PIN lists');
  }

  return result;
}

/**
 * Fetch single PIN List by ID
 */
export async function fetchPinListById(id: string): Promise<PinListResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pin-lists/${id}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch PIN list');
  }

  const result = await response.json();

  if (!result?.success || !result?.data) {
    throw new Error('Invalid response');
  }

  return result.data;
}

/**
 * Create new PIN List
 */
export async function createPinList(
  payload: CreatePinListPayload
): Promise<{ success: boolean; data: PinList; message?: string }> {
  const response = await fetch(
    buildApiUrl('/api/telephony/pin-lists'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload)
    }
  );

  const result = await response.json();

  if (!response.ok || !result?.success) {
    throw new Error(result?.message || 'Failed to create PIN list');
  }

  return result;
}

/**
 * Update PIN List
 */
export async function updatePinList(
  id: string,
  payload: UpdatePinListPayload
): Promise<{ success: boolean; data: PinList; message?: string }> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pin-lists/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload)
    }
  );

  const result = await response.json();

  if (!response.ok || !result?.success) {
    throw new Error(result?.message || 'Failed to update PIN list');
  }

  return result;
}

/**
 * Delete PIN List
 */
export async function deletePinList(id: string): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pin-lists/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    let message = 'Failed to delete PIN list';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }
}

/**
 * Export PIN Lists as CSV
 */
export async function exportPinLists(): Promise<Blob> {
  const response = await fetch(
    buildApiUrl('/api/telephony/pin-lists/export'),
    {
      method: 'GET',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to export PIN lists');
  }

  return await response.blob();
}

